import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaPlus, FaEye, FaPencilAlt } from 'react-icons/fa';
import ArticleEditor from '../../../components/editor/ArticleEditor';
import axiosPost from '../../../helpers/axios-helper';

export default function Articles() {
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [isCreatingAdvanced, setIsCreatingAdvanced] = useState(false);
  const [advancedEditorData, setAdvancedEditorData] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    author: '',
    date_published: '',
    image: '',
    category: '',
    tags: [],
    featured: false
  });

  // Available categories for selection
  const categories = ['Travel', 'Events', 'Culture', 'Food', 'Travel Tips', 'Nightlife', 'Accommodation', 'Community'];

  const fetchArticles = () => {
    setIsLoading(true);
    axiosPost(
      'articles/list',
      {},
      { page: 1, per_page: 20 },
      (res) => {
        const items = res?.data?.items || [];
        const mapped = items.map(it => ({
          id: it.id,
          title: it.title,
          excerpt: it.excerpt,
          content: '',
          author: '-',
          date_published: it.date_published ? new Date(it.date_published * 1000).toISOString().split('T')[0] : '',
          image: it.featured_image_url || '',
          category: it.category || '',
          status: it.status || 'draft',
          tags: [],
          featured: false
        }));
        setArticles(mapped);
        setIsLoading(false);
      }
    );
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  const handleEdit = (article) => {
    // Fetch full article and open advanced editor
    axiosPost(
      'articles/get',
      {},
      { id: article.id },
      (res) => {
        const full = res?.data || {};
        // Map backend response to ArticleEditor expected initialData
        const initial = {
          id: full.id,
          title: full.title,
          excerpt: full.excerpt,
          image: full.featured_image_url || '',
          category: full.category || 'Travel',
          country_id: full.country_id || '',
          tags: Array.isArray(full.tags) ? full.tags : [],
          blocks: Array.isArray(full.blocks) ? full.blocks.map((b, idx) => ({
            id: b.id || `block-${full.id || 'new'}-${idx}`,
            type: b.type,
            content: b.content
          })) : [],
          date_published: full.date_published || Math.floor(Date.now() / 1000),
          status: full.status || 'draft',
          author: full.author || '-'
        };
        setAdvancedEditorData(initial);
        setIsCreatingAdvanced(true);
        setIsEditing(false);
        setIsViewing(false);
      }
    );
  };

  const handleView = (article) => {
    setSelectedArticle(article);
    setIsViewing(true);
    setIsEditing(false);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this article?')) {
      setArticles(articles.filter(article => article.id !== id));
    }
  };

  const handleAddNew = () => {
    setSelectedArticle(null);
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      author: '',
      date_published: new Date().toISOString().split('T')[0],
      image: '',
      category: 'Travel',
      tags: '',
      featured: false
    });
    setIsEditing(true);
    setIsViewing(false);
    setIsCreatingAdvanced(false);
  };
  
  const handleAddNewAdvanced = () => {
    setSelectedArticle(null);
    setIsEditing(false);
    setIsViewing(false);
    setIsCreatingAdvanced(true);
  };
  
  const handleAdvancedEditorSave = (articleData) => {
    // After save (create or update), refresh the list and close editor
    setIsCreatingAdvanced(false);
    setAdvancedEditorData(null);
    fetchArticles();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Convert tags string to array
    const tagsArray = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
    
    const processedData = {
      ...formData,
      tags: tagsArray
    };
    
    if (selectedArticle) {
      // Update existing article
      setArticles(articles.map(article => 
        article.id === selectedArticle.id ? { ...processedData, id: article.id } : article
      ));
    } else {
      // Add new article
      const newId = Math.max(...articles.map(a => a.id), 0) + 1;
      setArticles([...articles, { ...processedData, id: newId }]);
    }
    
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsViewing(false);
    setIsCreatingAdvanced(false);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div>
      <div className="DashboardPanel">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 className="DashboardTitle">Articles Management</h1>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="DashboardButton" onClick={handleAddNew}>
              <FaPlus style={{ marginRight: '5px' }} /> Simple Article
            </button>
            <button className="DashboardButton" onClick={handleAddNewAdvanced} style={{ backgroundColor: '#6a8fff' }}>
              <FaPencilAlt style={{ marginRight: '5px' }} /> Advanced Editor
            </button>
          </div>
        </div>

        {isLoading ? (
          <p>Loading articles...</p>
        ) : isCreatingAdvanced ? (
          <ArticleEditor 
            initialData={advancedEditorData} 
            onSave={handleAdvancedEditorSave} 
          />
        ) : isEditing ? (
          <form className="DashboardForm" onSubmit={handleSubmit}>
            <h2 className="DashboardSubtitle">
              {selectedArticle ? 'Edit Article' : 'Add New Article'}
            </h2>
            
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="DashboardFormInput"
                required
              />
            </div>
            
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Excerpt</label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                className="DashboardFormInput"
                rows="2"
                required
              />
            </div>
            
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Content</label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleChange}
                className="DashboardFormInput"
                rows="10"
                required
              />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="DashboardFormGroup">
                <label className="DashboardFormLabel">Author</label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleChange}
                  className="DashboardFormInput"
                  required
                />
              </div>
              
              <div className="DashboardFormGroup">
                <label className="DashboardFormLabel">Publication Date</label>
                <input
                  type="date"
                  name="date_published"
                  value={formData.date_published}
                  onChange={handleChange}
                  className="DashboardFormInput"
                  required
                />
              </div>
            </div>
            
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Featured Image URL</label>
              <input
                type="text"
                name="image"
                value={formData.image}
                onChange={handleChange}
                className="DashboardFormInput"
                placeholder="https://example.com/image.jpg"
              />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="DashboardFormGroup">
                <label className="DashboardFormLabel">Category</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="DashboardFormInput"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              
              <div className="DashboardFormGroup">
                <label className="DashboardFormLabel">Tags (comma separated)</label>
                <input
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleChange}
                  className="DashboardFormInput"
                  placeholder="travel, beaches, lgbtq-friendly"
                />
              </div>
            </div>
            
            <div className="DashboardFormGroup" style={{ display: 'flex', alignItems: 'center' }}>
              <label>
                <input
                  type="checkbox"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleChange}
                  style={{ marginRight: '10px' }}
                />
                Featured Article
              </label>
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button type="button" className="DashboardButtonSecondary" onClick={handleCancel}>
                Cancel
              </button>
              <button type="submit" className="DashboardButton">
                {selectedArticle ? 'Update Article' : 'Add Article'}
              </button>
            </div>
          </form>
        ) : isViewing && selectedArticle ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 className="DashboardSubtitle">Article Preview</h2>
              <button className="DashboardButtonSecondary" onClick={handleCancel}>
                Back to List
              </button>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              {selectedArticle.image && (
                <img 
                  src={selectedArticle.image} 
                  alt={selectedArticle.title}
                  style={{ 
                    width: '100%', 
                    maxHeight: '300px', 
                    objectFit: 'cover', 
                    borderRadius: '8px',
                    marginBottom: '20px'
                  }}
                />
              )}
              
              <h1 style={{ 
                fontFamily: 'Lato, sans-serif',
                fontSize: '28px',
                fontWeight: '700',
                marginBottom: '10px'
              }}>
                {selectedArticle.title}
              </h1>
              
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '20px',
                color: '#666'
              }}>
                <span style={{ marginRight: '15px' }}>By {typeof selectedArticle.author === 'object' ? selectedArticle.author?.name : selectedArticle.author}</span>
                <span style={{ marginRight: '15px' }}>|</span>
                <span>{formatDate(selectedArticle.date_published)}</span>
                {selectedArticle.featured && (
                  <>
                    <span style={{ marginRight: '15px', marginLeft: '15px' }}>|</span>
                    <span style={{ 
                      background: '#1976d2', 
                      color: 'white', 
                      padding: '3px 8px', 
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      Featured
                    </span>
                  </>
                )}
              </div>
              
              <div style={{ 
                fontStyle: 'italic', 
                marginBottom: '20px',
                fontSize: '18px',
                color: '#444'
              }}>
                {selectedArticle.excerpt}
              </div>
              
              <div style={{ 
                lineHeight: '1.8',
                fontSize: '16px',
                whiteSpace: 'pre-line'
              }}>
                {selectedArticle.content}
              </div>
              
              <div style={{ marginTop: '30px' }}>
                <div style={{ fontWeight: '600', marginBottom: '5px' }}>Category:</div>
                <div style={{ 
                  display: 'inline-block',
                  background: '#f0f0f0',
                  padding: '5px 10px',
                  borderRadius: '4px',
                  marginBottom: '15px'
                }}>
                  {selectedArticle.category}
                </div>
                
                <div style={{ fontWeight: '600', marginBottom: '5px' }}>Tags:</div>
                <div>
                  {selectedArticle.tags.map(tag => (
                    <span 
                      key={tag}
                      style={{
                        display: 'inline-block',
                        background: '#e0e0e0',
                        padding: '3px 8px',
                        borderRadius: '4px',
                        margin: '0 5px 5px 0',
                        fontSize: '14px'
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button 
                className="DashboardButtonSecondary" 
                onClick={() => handleEdit(selectedArticle)}
              >
                <FaEdit style={{ marginRight: '5px' }} /> Edit Article
              </button>
            </div>
          </div>
        ) : (
          <table className="DashboardTable">
            <thead>
              <tr>
                <th>Title</th>
                <th>Author</th>
                <th>Date</th>
                <th>Category</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map(article => (
                <tr key={article.id}>
                  <td>{article.title}</td>
                  <td>{typeof article.author === 'object' ? article.author?.name : article.author}</td>
                  <td>{formatDate(article.date_published)}</td>
                  <td>{article.category}</td>
                  <td>
                    <span style={{
                      display: 'inline-block',
                      padding: '3px 10px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 700,
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      background: article.status === 'published' ? '#e8f5e9' : '#fff3e0',
                      color: article.status === 'published' ? '#2e7d32' : '#e65100'
                    }}>
                      {article.status || 'draft'}
                    </span>
                  </td>
                  <td>
                    <button 
                      onClick={() => handleView(article)} 
                      style={{ background: 'none', cursor: 'pointer', marginRight: '10px' }}
                      title="View Article"
                    >
                      <FaEye color="#333333" />
                    </button>
                    <button 
                      onClick={() => handleEdit(article)} 
                      style={{ background: 'none', cursor: 'pointer', marginRight: '10px' }}
                      title="Edit Article"
                    >
                      <FaEdit color="#1976d2" />
                    </button>
                    <button 
                      onClick={() => handleDelete(article.id)} 
                      style={{ background: 'none', cursor: 'pointer' }}
                      title="Delete Article"
                    >
                      <FaTrash color="#e65100" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
